import React, { useEffect, useState } from "react";
import { ThreeDots } from "react-loader-spinner";
import { Link, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { getCourseProgress, updateCourseProgress } from "../../api/server";
import DashBoardSidebar from "../../layouts/dashBoard-Sidebar";
import DashboardNavbar from "../../layouts/dashboardNavbar";

const FIRST_COURSE_LESSON_COUNT = 14;
const FIRST_COURSE_EVALUATION_CLASS = 15;
const SECOND_COURSE_PROGRESS_COUNT = 51;

const CLASS_FIVE_SUBCLASSES = [
  { number: 1, label: "Budget Analysis" },
  { number: 2, label: "About Your Household" },
  { number: 3, label: "Monthly Income" },
  { number: 4, label: "Monthly Expenses" },
  { number: 5, label: "Debts" },
  { number: 6, label: "Legal Actions and Collections" },
  { number: 7, label: "Assets" },
  { number: 8, label: "Review and Confirm" },
  { number: 9, label: "Report" },
];

const isChecked = (value) =>
  ["checked", "complete", "completed"].includes(
    String(value || "").toLowerCase()
  );

const buildMainClassPayload = (record = {}) => {
  return Object.fromEntries(
    Object.entries(record).filter(([field]) => field !== "classFiveSubclasses")
  );
};

const buildClassFiveSubclassPayload = (record = {}) => {
  const subclassProgress = CLASS_FIVE_SUBCLASSES.reduce(
    (result, subclass) => ({
      ...result,
      [`aud5_${subclass.number}`]:
        record.classFiveSubclasses?.[`aud5_${subclass.number}`] || "",
    }),
    {}
  );

  return {
    courseId: record.id,
    userid: record.userid,
    ...subclassProgress,
  };
};

const ProgressCheckbox = ({ id, checked, label, onChange }) => (
  <div className="iagree_radio course-progress-checkbox">
    <input
      type="checkbox"
      checked={checked}
      id={id}
      aria-label={label}
      onChange={(event) => onChange(event.target.checked)}
    />
    <label htmlFor={id}> </label>
  </div>
);

const CoursePrograme = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const userId = queryParams.get("id");
  const course = queryParams.get("course");
  const retake = Boolean(
    queryParams.get("retake") !== null &&
      queryParams.get("retake") !== "null" &&
      queryParams.get("retake") !== "false"
  );
  const [response, setResponse] = useState();
  const [getloading, setGetLoading] = useState(true);
  const [postLoading, setPostLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await getCourseProgress(userId, {
          course,
          retake: retake ? 1 : 0,
        });
        setResponse((result.data.data || []).map((record) => record || {}));
      } catch (error) {
        toast.error(
          error.response?.data?.error || "Unable to load course progress"
        );
      } finally {
        setGetLoading(false);
      }
    };

    fetchData();
  }, [course, retake, userId]);

  const hasJointFiler = Boolean(
    response?.[1] && Object.keys(response[1]).length > 0
  );

  const updateFiler = (filerIndex, updater) => {
    setResponse((current) =>
      current.map((record, index) =>
        index === filerIndex ? updater(record) : record
      )
    );
  };

  const toggleLesson = (filerIndex, lesson, checked) => {
    updateFiler(filerIndex, (record) => {
      if (course === "first" && lesson === 5) {
        const classFiveSubclasses = CLASS_FIVE_SUBCLASSES.reduce(
          (progress, subclass) => ({
            ...progress,
            [`aud5_${subclass.number}`]: checked ? "complete" : "",
          }),
          { ...(record.classFiveSubclasses || {}) }
        );

        return {
          ...record,
          aud5: checked ? "complete" : "",
          classFiveSubclasses,
        };
      }

      return {
        ...record,
        [`aud${lesson}`]: checked ? "complete" : "",
      };
    });
  };

  const toggleClassFiveSubclass = (filerIndex, subclassNumber, checked) => {
    updateFiler(filerIndex, (record) => {
      const classFiveSubclasses = {
        ...(record.classFiveSubclasses || {}),
        [`aud5_${subclassNumber}`]: checked ? "complete" : "",
      };
      const allSubclassesComplete = CLASS_FIVE_SUBCLASSES.every((subclass) =>
        isChecked(classFiveSubclasses[`aud5_${subclass.number}`])
      );

      return {
        ...record,
        aud5: allSubclassesComplete ? "complete" : "",
        classFiveSubclasses,
      };
    });
  };

  const markAll = (filerIndex) => {
    updateFiler(filerIndex, (record) => {
      const lessonCount =
        course === "first"
          ? FIRST_COURSE_LESSON_COUNT
          : SECOND_COURSE_PROGRESS_COUNT;
      const lessons = Array.from({ length: lessonCount }, (_, index) => index + 1)
        .reduce(
          (result, lesson) => ({
            ...result,
            [`aud${lesson}`]: "complete",
          }),
          {}
        );

      if (course !== "first") {
        return { ...record, ...lessons, completed: "checked" };
      }

      const classFiveSubclasses = CLASS_FIVE_SUBCLASSES.reduce(
        (result, subclass) => ({
          ...result,
          [`aud5_${subclass.number}`]: "complete",
        }),
        { ...(record.classFiveSubclasses || {}) }
      );

      return {
        ...record,
        ...lessons,
        [`aud${FIRST_COURSE_EVALUATION_CLASS}`]: "complete",
        classFiveSubclasses,
        completed: "checked",
      };
    });
  };

  const toggleCompleted = (filerIndex, checked) => {
    updateFiler(filerIndex, (record) => ({
      ...record,
      completed: checked ? "complete" : "",
    }));
  };

  const handleUpdate = async () => {
    setPostLoading(true);
    try {
      const filerRecords = response.filter((record) => record?.id);
      const data =
        course === "first"
          ? {
              mainClasses: filerRecords.map(buildMainClassPayload),
              classFiveSubClass: filerRecords.map(
                buildClassFiveSubclassPayload
              ),
            }
          : [response[0], response[1]];

      await updateCourseProgress(userId, {
        course,
        retake: retake ? 1 : 0,
        data,
      });
      toast.success("Course updated successfully");
    } catch (error) {
      toast.error(
        error.response?.data?.error || "Unable to update course progress"
      );
    } finally {
      setPostLoading(false);
    }
  };

  const renderFilerCheckbox = (filerIndex, lesson) => {
    const filerLabel = filerIndex === 0 ? "Main filer" : "Joint filer";
    return (
      <ProgressCheckbox
        id={`filer-${filerIndex}-class-${lesson}`}
        checked={isChecked(response[filerIndex]?.[`aud${lesson}`])}
        label={`${filerLabel}, class ${lesson}`}
        onChange={(checked) => toggleLesson(filerIndex, lesson, checked)}
      />
    );
  };

  const renderClassFiveSubclassCheckbox = (filerIndex, subclass) => {
    const filerLabel = filerIndex === 0 ? "Main filer" : "Joint filer";
    return (
      <ProgressCheckbox
        id={`filer-${filerIndex}-class-5-step-${subclass.number}`}
        checked={isChecked(
          response[filerIndex]?.classFiveSubclasses?.[
            `aud5_${subclass.number}`
          ]
        )}
        label={`${filerLabel}, class 5 step ${subclass.number}: ${subclass.label}`}
        onChange={(checked) =>
          toggleClassFiveSubclass(filerIndex, subclass.number, checked)
        }
      />
    );
  };

  const renderFirstCourseRows = () =>
    Array.from({ length: FIRST_COURSE_LESSON_COUNT }, (_, index) => index + 1)
      .map((lesson) => (
        <React.Fragment key={lesson}>
          <tr className={lesson === 5 ? "course-progress-class-five" : ""}>
            <td>
              <div className="course-progress-lesson-label">
                <span className="course-progress-lesson-number">
                  {String(lesson).padStart(2, "0")}
                </span>
                {lesson === 5 && (
                  <>
                    <span>Class 5</span>
                    <span className="course-progress-count-badge">
                      9 steps
                    </span>
                  </>
                )}
              </div>
            </td>
            <td>{renderFilerCheckbox(0, lesson)}</td>
            {hasJointFiler && <td>{renderFilerCheckbox(1, lesson)}</td>}
          </tr>

          {lesson === 5 &&
            CLASS_FIVE_SUBCLASSES.map((subclass) => (
              <tr
                className="course-progress-subclass-row"
                key={`class-five-${subclass.number}`}
              >
                <td>
                  <div className="course-progress-subclass-label">
                    <span>{`5.${subclass.number}`}</span>
                    <span>{subclass.label}</span>
                  </div>
                </td>
                <td>{renderClassFiveSubclassCheckbox(0, subclass)}</td>
                {hasJointFiler && (
                  <td>{renderClassFiveSubclassCheckbox(1, subclass)}</td>
                )}
              </tr>
            ))}
        </React.Fragment>
      ));

  const renderSecondCourseRows = () =>
    Array.from({ length: SECOND_COURSE_PROGRESS_COUNT }, (_, index) => index + 1)
      .map((lesson) => (
        <tr key={lesson}>
          <td>{String(lesson).padStart(2, "0")}</td>
          <td>{renderFilerCheckbox(0, lesson)}</td>
          {hasJointFiler && <td>{renderFilerCheckbox(1, lesson)}</td>}
        </tr>
      ));

  return (
    <div>
      <div className="wrapper">
        <DashBoardSidebar />
        <div id="content">
          <DashboardNavbar />
          <section className="manage-students course-progress admin-course-progress-page">
            <div className="card">
              <div className="card-header">
                <div className="row align-items-center g-3">
                  <div className="col-lg-6">
                    <h4 className="card-title mb-1">Course Progress</h4>
                    {course === "first" && (
                      <p className="course-progress-structure-note mb-0">
                        14 classes · Class 5 has 9 steps
                      </p>
                    )}
                  </div>
                  {response && (
                    <div className="col-lg-6 course-progress-header-actions">
                      <button
                        className="btn_brnd"
                        type="button"
                        onClick={() => markAll(0)}
                      >
                        Click All
                      </button>
                      {hasJointFiler && (
                        <button
                          className="btn_brnd"
                          type="button"
                          onClick={() => markAll(1)}
                        >
                          Click All (Joint)
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="card-body">
                {getloading ? (
                  <ThreeDots
                    height="40"
                    width="80"
                    radius="9"
                    color="#4fa94d"
                    ariaLabel="three-dots-loading"
                    wrapperStyle={{ display: "flex", justifyContent: "center" }}
                    wrapperClassName="spinner"
                    visible
                  />
                ) : (
                  response && (
                    <>
                      <div className="course-progress-table-wrap">
                        <table className="table admin-course-progress-table">
                          <thead>
                            <tr>
                              <th scope="col">Class / Step</th>
                              <th scope="col">Main Filer</th>
                              {hasJointFiler && (
                                <th scope="col">Joint Filer</th>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {course === "first"
                              ? renderFirstCourseRows()
                              : renderSecondCourseRows()}

                            {course === "first" && (
                              <tr>
                                <td>
                                  <div className="course-progress-lesson-label">
                                    <span className="course-progress-lesson-number">
                                      {FIRST_COURSE_EVALUATION_CLASS}
                                    </span>
                                  </div>
                                </td>
                                <td>
                                  {renderFilerCheckbox(
                                    0,
                                    FIRST_COURSE_EVALUATION_CLASS
                                  )}
                                </td>
                                {hasJointFiler && (
                                  <td>
                                    {renderFilerCheckbox(
                                      1,
                                      FIRST_COURSE_EVALUATION_CLASS
                                    )}
                                  </td>
                                )}
                              </tr>
                            )}

                            <tr className="course-progress-completed-row">
                              <td>Completed (Show in Certificate List)</td>
                              <td>
                                <ProgressCheckbox
                                  id="main-course-completed"
                                  checked={isChecked(response[0]?.completed)}
                                  label="Main filer, show in certificate list"
                                  onChange={(checked) =>
                                    toggleCompleted(0, checked)
                                  }
                                />
                              </td>
                              {hasJointFiler && (
                                <td>
                                  <ProgressCheckbox
                                    id="joint-course-completed"
                                    checked={isChecked(response[1]?.completed)}
                                    label="Joint filer, show in certificate list"
                                    onChange={(checked) =>
                                      toggleCompleted(1, checked)
                                    }
                                  />
                                </td>
                              )}
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      <div className="text-center">
                        <Link to={`/editstudent?id=${userId}`}>
                          <button
                            className="btn_brnd-outline my-2 me-3"
                            type="button"
                          >
                            Back
                          </button>
                        </Link>
                        <button
                          className="btn_brnd my-3"
                          type="button"
                          onClick={handleUpdate}
                          disabled={postLoading}
                        >
                          {postLoading ? (
                            <ThreeDots
                              height="20"
                              width="60"
                              radius="9"
                              color="#4fa94d"
                              ariaLabel="three-dots-loading"
                              wrapperStyle={{ display: "block" }}
                              wrapperClassName="spinner"
                              visible
                            />
                          ) : (
                            "Update"
                          )}
                        </button>
                      </div>
                    </>
                  )
                )}
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default CoursePrograme;
